import { useEffect, useRef, useState } from 'react'
import { Send, Loader2 } from 'lucide-react'
import { useCanonStore } from '@/stores/useCanonStore'

// canon — scaffold PLACEHOLDER page. The frontend foundation breathes (renders,
// connects, streams); the node-builder FILLS this to deliver the doctrine's emotional
// product — the decisive thing on top. See decomposing-workflows/reference/frontend.md.

export function CanonPage() {
  const messages = useCanonStore((s) => s.messages)
  const streaming = useCanonStore((s) => s.streaming)
  const hydrated = useCanonStore((s) => s.hydrated)
  const error = useCanonStore((s) => s.error)
  const sendMessage = useCanonStore((s) => s.sendMessage)
  const loadSession = useCanonStore((s) => s.loadSession)
  const [draft, setDraft] = useState('')
  const didLoad = useRef(false)

  useEffect(() => {
    if (didLoad.current) return
    didLoad.current = true
    void loadSession()
  }, [loadSession])

  async function handleSend() {
    const content = draft.trim()
    if (!content || streaming || !hydrated) return
    setDraft('')
    await sendMessage(content)
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto flex max-w-3xl flex-col gap-4 px-6 py-8 md:px-8">
          <h1 className="text-2xl font-light tracking-tight text-foreground">canon</h1>
          <p className="-mt-2 text-sm font-light text-muted-foreground">
            Foundation scaffolded by{' '}
            <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[0.8em]">/commissioning-workflows</code>.
            The node-builder fills this to deliver the emotional product.
          </p>

          {!hydrated ? (
            <p className="mt-8 text-center text-sm font-light text-muted-foreground">
              Loading session…
            </p>
          ) : messages.length === 0 ? (
            <p className="mt-8 text-center text-sm font-light text-muted-foreground">
              Say something to exercise the stream.
            </p>
          ) : (
            <ul className="mt-4 space-y-3">
              {messages.map((m) => (
                <li
                  key={m.id}
                  className={
                    m.role === 'user'
                      ? 'ml-auto max-w-[80%] rounded-lg bg-foreground px-4 py-2.5 text-sm text-background'
                      : 'mr-auto max-w-[80%] rounded-lg border border-border bg-card px-4 py-2.5 text-sm font-light text-foreground'
                  }
                >
                  {m.content || (
                    <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" /> thinking…
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}

          {error && (
            <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          )}
        </div>
      </div>

      <div className="border-t border-border bg-background">
        <div className="mx-auto max-w-3xl px-6 py-4 md:px-8">
          <div className="flex items-end gap-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  handleSend()
                }
              }}
              rows={1}
              disabled={!hydrated}
              placeholder="Message canon…"
              className="max-h-40 flex-1 resize-none rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-foreground/30"
            />
            <button
              onClick={handleSend}
              disabled={!hydrated || streaming || !draft.trim()}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-card text-muted-foreground transition-colors hover:bg-muted disabled:opacity-40"
              aria-label="Send"
            >
              {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
